import { computed, inject, onMounted, onUnmounted, provide, ref } from 'vue'

const TABLET_BREAKPOINT = 834
const DESKTOP_BREAKPOINT = 1280

const checkBreakpointValue = (targetBreakpoint: number, innerWidth: number): boolean => {
  if (innerWidth >= DESKTOP_BREAKPOINT) {
    return innerWidth >= targetBreakpoint
  } else if (innerWidth >= TABLET_BREAKPOINT) {
    return innerWidth >= targetBreakpoint
  } else {
    return false
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useBaseBreakpoints () {
  const style = getComputedStyle(document.documentElement)
  const breakpointWidth = ref(0)

  const isTabletSize = computed((): boolean => checkBreakpointValue(TABLET_BREAKPOINT, breakpointWidth.value))
  const isDesktopSize = computed((): boolean => checkBreakpointValue(DESKTOP_BREAKPOINT, breakpointWidth.value))
  const isMobile = computed((): boolean => !(isTabletSize.value || isDesktopSize.value))

  const onResize = (): void => {
    if (style === null) {
      return
    }

    breakpointWidth.value = Number(style.getPropertyValue('--breakpoint-width'))
  }

  onMounted(() => {
    window?.addEventListener('resize', onResize)
  })

  onUnmounted(() => {
    window?.removeEventListener('resize', onResize)
  })

  onResize()

  return {
    isMobile,
    isTabletSize,
    isDesktopSize
  }
}

const defaultKey = Symbol('global:breakpoints')

export default function useBreakpoints (): ReturnType<typeof useBaseBreakpoints> {
  const result = inject<ReturnType<typeof useBaseBreakpoints> | undefined>(defaultKey, undefined)

  if (result === undefined) {
    const newResult = useBaseBreakpoints()
    provide(defaultKey, newResult)
    return newResult
  }

  return result
}
